import { Link } from 'react-router-dom';
import { useOrg } from '../../api/app-context';
import { useFactClaims } from '../../api/hooks';
import { loadErrorLine } from '../../api/errors';
import { sourceTierLabel } from '../../lib/copy';
import { formatDate, formatDay, joinClauses } from '../../lib/format';
import { Loading } from '../ui/loading';

/**
 * The citation drill-down (ui-design §4.7). Citations resolve to **stored
 * evidence**, never live URLs — the url is quiet plain text, not a link target.
 * Corroboration attached after a row was removed is marked as such: it does not
 * resurrect anything.
 */
export function Claims({ lineageId }: { lineageId: string }) {
  const claims = useFactClaims(lineageId);
  /* The first-party tier reads in the org's own words (`sourceTierLabel`), so
     the vertical is needed wherever a tier is printed. */
  const { vertical, is_demo, id: orgId } = useOrg();

  if (claims.isPending)
    return (
      <div className="claims">
        <Loading />
      </div>
    );
  if (claims.isError) return <div className="claims">{loadErrorLine(claims.error)}</div>;
  if (claims.data.entries.length === 0) {
    return <div className="claims">No stored sources.</div>;
  }

  return (
    <div className="claims">
      {claims.data.entries.map((claim) => (
        <div className="claim-row" key={claim.claim_id}>
          {claim.evidence_excerpt ? <div className="excerpt">{claim.evidence_excerpt}</div> : null}
          <div className="meta">
            {joinClauses([
              sourceTierLabel(vertical, claim.source_tier),
              claim.content_date ? formatDate(claim.content_date) : null,
              claim.content_date_basis === 'inferred' ? 'date read from the source' : null,
              claim.content_date_basis === 'absent' ? 'undated' : null,
              claim.retrieved_at ? `retrieved ${formatDay(claim.retrieved_at)}` : null,
              claim.url,
              claim.post_negation ? 'arrived after this was removed' : null,
            ])}
          </div>
          {is_demo && claim.evidence_id && (
            <Link to={`/demo/evidence/${claim.evidence_id}?org=${orgId}`}>
              Open the stored document
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}
